import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { Button, IconButton } from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import ModeEditOutlineSharpIcon from '@mui/icons-material/ModeEditOutlineSharp';
import BookmarkSharpIcon from '@mui/icons-material/BookmarkSharp';
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import Loader from "../Loader";
import host from "../../global";
import DeleteIcon from "@mui/icons-material/Delete";
import { confirmCompanyDetails } from "../../utils/companyProfileCheck";

Modal.setAppElement("#root");

function OrgAdminBulkResumeSummary() {
  const [shortlisted, setShortlisted] = useState([]);
  const [retrofit, setRetrofit] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [invalidData, setInvalidData] = useState([]);
  const [candidateUniqueID, setCandidateUniqueID] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const { upload_id } = useParams();
  const navigate = useNavigate();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const accessToken = sessionStorage.getItem("accessToken");
  const [resumeScoreThreshold, setResumeScoreThreshold] = useState(60);
  const [selectedShortlistedCandidates, setSelectedShortlistedCandidates] = useState([]);
  const [selectAllShortlisted, setSelectAllShortlisted] = useState(false);
  const [selectAllRetrofit, setSelectAllRetrofit] = useState(false);

  useEffect(() => {
    // Clean up old localStorage flags
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("results_empty_") && !key.includes(upload_id)) {
        localStorage.removeItem(key);
      }
    });

    // Check if results are empty for this upload_id
    const isResultsEmpty = localStorage.getItem(`results_empty_${upload_id}`);
    if (isResultsEmpty === "true") {
      setScheduleDialogOpen(true);
      return;
    }

    const fetchCandidates = async () => {
      try {
        const response = await axios.get(`${host}/resumes`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            upload_id: upload_id,
          },
        });
        const fetchedCandidates = response.data.data;
        let score_threshold = response.data.resume_score_threshold;
        setResumeScoreThreshold(score_threshold);

        if (Array.isArray(fetchedCandidates)) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const isValidEmail = (email) => {
            return email && typeof email === "string" && emailRegex.test(email);
          };
          const shortlistedCandidates = fetchedCandidates.filter(
            (c) => c.score >= score_threshold && isValidEmail(c.candidate_email)
          );
          const retrofitCandidates = fetchedCandidates.filter(
            (c) =>
              c.score < score_threshold &&
              c.score !== null &&
              isValidEmail(c.candidate_email)
          );
          const invalidCandidates = fetchedCandidates.filter(
            (c) =>
              !isValidEmail(c.candidate_email) ||
              c.score === null ||
              c.candidate_email === "not provided" ||
              c.candidate_email === ""
          );

          setShortlisted(shortlistedCandidates);
          setRetrofit(retrofitCandidates);
          setInvalidData(invalidCandidates);
          setIsLoading(false);

          if (
            shortlistedCandidates.length === 0 &&
            retrofitCandidates.length === 0 &&
            invalidCandidates.length === 0
          ) {
            localStorage.setItem(`results_empty_${upload_id}`, "true");
            setScheduleDialogOpen(true);
          }
        } else {
          console.error("Unexpected data format:", fetchedCandidates);
          toast.error("Invalid data format received from server.");
          setError("Invalid data format");
        }
      } catch (error) {
        if (error.response) {
          const status = error.response.status;
          const detail = error.response.data.detail;
          if (status === 401) {
            sessionStorage.removeItem("accessToken");
            toast.error(`Unauthorized: ${detail}`);
            setError("Unauthorized");
          } else if (status === 404 || status === 500) {
            localStorage.setItem(`results_empty_${upload_id}`, "true");
            setScheduleDialogOpen(true);
          } else {
            toast.error(`Error: ${detail}`);
            setError(detail);
          }
        } else {
          toast.error("An unexpected error occurred. Please try again.");
          setError("Unexpected error");
        }
      }
    };
    fetchCandidates();
  }, [upload_id, accessToken]);

  // Auto-navigate to dashboard after 3 seconds when dialog is open
  useEffect(() => {
    if (scheduleDialogOpen) {
      const timer = setTimeout(() => {
        setScheduleDialogOpen(false);
        navigate("/org-admin/dashboard");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [scheduleDialogOpen, navigate]);

  const getRowStyle = (profile_type) => ({
    backgroundColor: profile_type === 'duplicate' ? 'rgba(255, 0, 0, 0.2)' : 'transparent'
  });

  const renderTableRow = (candidate, index, arrayType) => {
    const isEditing = candidateUniqueID === candidate.unique_id;

    return (
      <Tr key={index} style={getRowStyle(candidate.profile_type)}>
        {arrayType !== 'invalid' && (
          <Td style={{ width: "60px", textAlign: "center", paddingLeft: "40px" }}>
            <input
              type="checkbox"
              checked={arrayType === 'shortlisted'
                ? selectedShortlistedCandidates.includes(candidate)
                : selectedCandidates.includes(candidate)}
              onChange={() => arrayType === 'shortlisted'
                ? handleShortlistCheckboxChange(candidate)
                : handleCheckboxChange(candidate)}
            />
          </Td>
        )}
        <Td style={{ width: "130px" }}>{index + 1}</Td>
        <Td style={{ width: "220px", textAlign: "left" }}>{candidate.name}</Td>
        <Td style={{ width: "350px", textAlign: "left" }}>
          {isEditing ? (
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter email"
              required
              className="bg-transparent w-full"
              style={{
                border: "1px solid white",
                outline: "none",
                color: "white",
                textAlign: "left"
              }}
            />
          ) : (
            candidate.candidate_email || "Not Provided"
          )}
        </Td>
        <Td className="text-center" style={{ width: "100px" }}>
          {candidate.score || "N/A"}
        </Td>
        <Td className="text-center" style={{ width: "100px" }}>
          <div className="tooltip-icon" onClick={() => handleReportClick(candidate)}>
            <VisibilityRoundedIcon style={{ height: "30px", width: "30px" }} />
            <span className="tooltip-text">View Report</span>
          </div>
        </Td>
        <Td style={{ width: "100px" }}>
          <div className="flex items-center justify-center space-x-2">
            {isEditing ? (
              <IconButton
                onClick={() => saveEmailChange(candidate)}
                color="primary"
              >
                <BookmarkSharpIcon />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => {
                  setNewEmail(candidate.candidate_email || "");
                  setCandidateUniqueID(candidate.unique_id);
                }}
                color="secondary"
              >
                <ModeEditOutlineSharpIcon />
              </IconButton>
            )}
            {arrayType === 'invalid' && (
              <IconButton
                onClick={() => deleteSingleCandidate(candidate)}
                style={{ color: 'white' }}
                onMouseEnter={(e) => e.target.style.color = 'red'}
                onMouseLeave={(e) => e.target.style.color = 'white'}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </div>
        </Td>
      </Tr>
    );
  };

  const scheduleInterviewForAll = async () => {
    const allSelectedCandidates = [
      ...selectedShortlistedCandidates,
      ...selectedCandidates,
    ];

    if (allSelectedCandidates.length === 0) {
      toast.error("Please select at least one candidate to schedule interviews.");
      return;
    }

    if (!(await confirmCompanyDetails("invite"))) return;

    const selectedEmails = allSelectedCandidates.map(
      (candidate) => candidate.candidate_email
    );
    const selectedUniqueIds = allSelectedCandidates.map(
      (candidate) => candidate.unique_id
    );

    try {
      // Step 1: Schedule interviews
      await axios.post(
        `${host}/schedule_interviews`,
        { unique_ids: selectedUniqueIds },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            upload_id: upload_id,
          },
        }
      );

      // Step 2: Delete scheduled candidates
      const deleteResponse = await axios.delete(`${host}/deletecandidateBulkUpload`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          upload_id: upload_id,
          emails: selectedEmails.join(","),
          unique_id: selectedUniqueIds.join(",")
        },
      });

      // Update state
      setShortlisted((prev) =>
        prev.filter((candidate) => !selectedUniqueIds.includes(candidate.unique_id))
      );
      setRetrofit((prev) =>
        prev.filter((candidate) => !selectedUniqueIds.includes(candidate.unique_id))
      );
      setSelectedShortlistedCandidates([]);
      setSelectedCandidates([]);
      toast.success(`Interviews scheduled for ${allSelectedCandidates.length} candidate(s).`);

      // Show popup if results are empty
      if (deleteResponse.data.results_empty) {
        localStorage.setItem(`results_empty_${upload_id}`, "true");
        setScheduleDialogOpen(true);
      }
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail;
        if (status === 401) {
          sessionStorage.removeItem("accessToken");
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404 || status === 500) {
          localStorage.setItem(`results_empty_${upload_id}`, "true");
          setScheduleDialogOpen(true);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
  };

  const determineProfileType = (type) => {
    if (type === "unique") {
      return <div className="status-completed">Unique</div>;
    } else {
      return <div className="status-in-progress">Duplicate</div>;
    }
  };

  const handleReportClick = (candidate) => {
    setCurrentReport(candidate.report);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setCurrentReport(null);
  };

  const saveEmailChange = async (candidate) => {
    if (!newEmail) {
      toast.error("Please enter a valid email");
      return;
    }

    if (newEmail === candidate.candidate_email) {
      toast.info("No changes made to email");
      setCandidateUniqueID(null);
      setNewEmail("");
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(newEmail)) {
      toast.error("Please enter a valid email address (e.g., example@gmail.com)");
      return;
    }

    try {
      const params = new URLSearchParams({
        upload_id: upload_id,
        unique_id: candidate.unique_id,
        new_email: newEmail,
      });

      await axios.put(
        `${host}/update_candidate_email`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: params,
        }
      );

      const updateCandidateInArray = (array) => {
        return array.map((c) => {
          if (c.unique_id === candidate.unique_id) {
            return { ...c, candidate_email: newEmail };
          }
          return c;
        });
      };

      setShortlisted(updateCandidateInArray(shortlisted));
      setRetrofit(updateCandidateInArray(retrofit));
      setInvalidData(updateCandidateInArray(invalidData));

      const hasInvalidEmail = invalidData.some(
        (c) => c.unique_id !== candidate.unique_id && !c.candidate_email
      );
      if (!hasInvalidEmail) {
        window.location.reload();
      }

      setCandidateUniqueID(null);
      setNewEmail("");
      toast.success("Email updated successfully!");
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail;
        if (status === 401) {
          sessionStorage.removeItem("accessToken");
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404 || status === 500) {
          localStorage.setItem(`results_empty_${upload_id}`, "true");
          setScheduleDialogOpen(true);
        } else if (status === 422) {
          toast.error(`Invalid request: ${detail}`);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
  };

  const handleCheckboxChange = (candidate) => {
    setSelectedCandidates((prevSelected) => {
      if (prevSelected.includes(candidate)) {
        return prevSelected.filter((c) => c !== candidate);
      } else {
        return [...prevSelected, candidate];
      }
    });
  };

  const handleShortlistCheckboxChange = (candidate) => {
    setSelectedShortlistedCandidates((prev) =>
      prev.includes(candidate)
        ? prev.filter((c) => c !== candidate)
        : [...prev, candidate]
    );
  };

  const handleSelectAllShortlisted = () => {
    if (selectAllShortlisted) {
      setSelectedShortlistedCandidates([]);
      setSelectAllShortlisted(false);
    } else {
      setSelectedShortlistedCandidates([...shortlisted]);
      setSelectAllShortlisted(true);
    }
  };

  const handleSelectAllRetrofit = () => {
    if (selectAllRetrofit) {
      setSelectedCandidates([]);
      setSelectAllRetrofit(false);
    } else {
      setSelectedCandidates([...retrofit]);
      setSelectAllRetrofit(true);
    }
  };

  useEffect(() => {
    setSelectAllShortlisted(
      shortlisted.length > 0 &&
      selectedShortlistedCandidates.length === shortlisted.length
    );
    setSelectAllRetrofit(
      retrofit.length > 0 &&
      selectedCandidates.length === retrofit.length
    );
  }, [selectedShortlistedCandidates, selectedCandidates, shortlisted, retrofit]);

  const deleteSelectedCandidates = async () => {
    const selectedEmails = selectedCandidates.map(
      (candidate) => candidate.candidate_email
    );

    const selectedUniqueIds = selectedCandidates.map(
      (candidate) => candidate.unique_id
    );

    try {
      const response = await axios.delete(`${host}/deletecandidateBulkUpload`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          upload_id: upload_id,
          emails: selectedEmails.join(","),
          unique_id: selectedUniqueIds.join(",")
        },
      });
      toast.success("Selected candidates deleted successfully");
      setRetrofit((prev) =>
        prev.filter((candidate) => !selectedUniqueIds.includes(candidate.unique_id))
      );
      setSelectedCandidates([]);
      if (response.data.results_empty) {
        localStorage.setItem(`results_empty_${upload_id}`, "true");
        setScheduleDialogOpen(true);
      }
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail;
        if (status === 401) {
          sessionStorage.removeItem("accessToken");
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404 || status === 500) {
          localStorage.setItem(`results_empty_${upload_id}`, "true");
          setScheduleDialogOpen(true);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
  };

  const deleteSingleCandidate = async (candidate) => {
    try {
      const response = await axios.delete(`${host}/deletecandidateBulkUpload`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          upload_id: upload_id,
          emails: candidate.candidate_email || "",
          unique_id: candidate.unique_id
        },
      });
      toast.success("Candidate deleted successfully");
      setInvalidData(invalidData.filter((c) => c !== candidate));
      if (response.data.results_empty) {
        localStorage.setItem(`results_empty_${upload_id}`, "true");
        setScheduleDialogOpen(true);
      }
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail;
        if (status === 401) {
          sessionStorage.removeItem("accessToken");
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404 || status === 500) {
          localStorage.setItem(`results_empty_${upload_id}`, "true");
          setScheduleDialogOpen(true);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
  };

  const deleteSelectedShortlistedCandidates = async () => {
    const selectedEmails = selectedShortlistedCandidates.map(
      (candidate) => candidate.candidate_email
    );

    const selectedUniqueIds = selectedShortlistedCandidates.map(
      (candidate) => candidate.unique_id
    );

    try {
      const response = await axios.delete(`${host}/deletecandidateBulkUpload`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          upload_id: upload_id,
          emails: selectedEmails.join(","),
          unique_id: selectedUniqueIds.join(",")
        },
      });
      setShortlisted((prev) =>
        prev.filter((candidate) => !selectedShortlistedCandidates.includes(candidate))
      );
      setSelectedShortlistedCandidates([]);
      toast.success("Selected candidates deleted successfully");
      if (response.data.results_empty) {
        localStorage.setItem(`results_empty_${upload_id}`, "true");
        setScheduleDialogOpen(true);
      }
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail;
        if (status === 401) {
          sessionStorage.removeItem("accessToken");
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404 || status === 500) {
          localStorage.setItem(`results_empty_${upload_id}`, "true");
          setScheduleDialogOpen(true);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
  };

  const shortlistSelectedCandidates = () => {
    const candidatesToShortlist = selectedCandidates.filter(
      (candidate) => candidate.score < resumeScoreThreshold
    );
    setRetrofit(retrofit.filter((c) => !candidatesToShortlist.includes(c)));
    setShortlisted([...shortlisted, ...candidatesToShortlist]);
    setSelectedCandidates([]);
  };

  if (error || scheduleDialogOpen) {
    return (
      <Dialog
        open={scheduleDialogOpen}
        PaperProps={{
          style: {
            background: "linear-gradient(233.57deg, rgba(34, 52, 97, 0.25) -5.01%, rgba(11, 10, 31, 0.05) 100%)",
            opacity: 1,
            backdropFilter: "blur(500px)",
          }
        }}
      >
        <DialogTitle className="text-white">All Interviews Scheduled</DialogTitle>
        <DialogContent dividers className="text-white mt-2">
          <p>No data to show. Redirecting to dashboard...</p>
        </DialogContent>
      </Dialog>
    );
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
      <div className="p-8 company-admin-container">
        <div className="rounded-lg content glass-effect">
          <div className="maincontent">
            <div className="container mx-auto px-4">
              <h1 className="text-white font-bold mb-4">Candidate Management</h1>
              {invalidData.length > 0 ? (
                <div className="table-container">
                  <div className="table-header">
                    <h2 className="text-white font-semibold mb-1">Invalid Data Candidates</h2>
                    <p className="text-red-400 font-light text-xs mb-3">
                      Please validate the invalid candidates before proceeding further.
                    </p>
                    <Table>
                      <Thead>
                        <Tr>
                          <Th className="custom-th whitespace-nowrap" style={{ width: "130px", textAlign: "center" }}>Sr. No</Th>
                          <Th className="custom-th whitespace-nowrap" style={{ width: "220px", textAlign: "left" }}>Candidate Name</Th>
                          <Th className="custom-th" style={{ width: "350px" }}>Email</Th>
                          <Th className="custom-th" style={{ width: "100px" }}>Score</Th>
                          <Th className="custom-th" style={{ width: "100px" }}>Report</Th>
                          <Th className="custom-th" style={{ width: "60px" }}>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {invalidData.map((candidate, index) => renderTableRow(candidate, index, 'invalid'))}
                      </Tbody>
                    </Table>
                  </div>
                </div>
              ) : (
                <>
                  {shortlisted.length > 0 && (
                    <div className="table-container">
                      <div className="table-header">
                        <h2 className="text-white font-semibold">Shortlisted Candidates</h2>
                        <div className="flex gap-4 py-4">
                          <button
                            className="button"
                            onClick={scheduleInterviewForAll}
                          >
                            <span>Schedule Interviews for Selected</span>
                          </button>
                          {selectedShortlistedCandidates.length > 0 && (
                            <button className="button" onClick={deleteSelectedShortlistedCandidates}>
                              <span>Delete Selected Candidates</span>
                            </button>
                          )}
                        </div>
                        <Table>
                          <Thead>
                            <Tr>
                              <Th className="custom-th" style={{ width: "60px" }}>
                                <input
                                  type="checkbox"
                                  checked={selectAllShortlisted}
                                  onChange={handleSelectAllShortlisted}
                                  title="Select All"
                                />
                              </Th>
                              <Th className="custom-th" style={{ width: "130px", textAlign: "center" }}>Sr. No</Th>
                              <Th className="custom-th whitespace-nowrap" style={{ width: "220px", textAlign: "left" }}>Candidate Name</Th>
                              <Th className="custom-th" style={{ width: "350px" }}>Email</Th>
                              <Th className="custom-th" style={{ width: "100px" }}>Score</Th>
                              <Th className="custom-th" style={{ width: "100px" }}>Report</Th>
                              <Th className="custom-th" style={{ width: "100px" }}>Edit</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {shortlisted.map((candidate, index) => renderTableRow(candidate, index, 'shortlisted'))}
                          </Tbody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {retrofit.length > 0 && (
                    <div className="table-container">
                      <div className="table-header">
                        <h2 className="font-semibold text-white mb-7">RetroFit Candidates</h2>
                        {selectedCandidates.length > 0 && (
                          <div className="flex gap-4 py-4">
                            <button
                              className="button"
                              onClick={deleteSelectedCandidates}
                            >
                              <span>Delete Selected Candidates</span>
                            </button>
                            <button
                              className="button"
                              onClick={shortlistSelectedCandidates}
                            >
                              <span>Shortlist Selected Candidates</span>
                            </button>
                          </div>
                        )}
                        <Table>
                          <Thead>
                            <Tr>
                              <Th className="custom-th" style={{ width: "60px" }}>
                                <input
                                  type="checkbox"
                                  checked={selectAllRetrofit}
                                  onChange={handleSelectAllRetrofit}
                                  title="Select All"
                                />
                              </Th>
                              <Th className="custom-th" style={{ width: "130px", textAlign: "center" }}>Sr. No</Th>
                              <Th className="custom-th whitespace-nowrap" style={{ width: "220px", textAlign: "left" }}>Candidate Name</Th>
                              <Th className="custom-th" style={{ width: "350px" }}>Email</Th>
                              <Th className="custom-th" style={{ width: "100px" }}>Score</Th>
                              <Th className="custom-th" style={{ width: "100px" }}>Report</Th>
                              <Th className="custom-th" style={{ width: "100px" }}>Edit</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {retrofit.map((candidate, index) => renderTableRow(candidate, index, 'retrofit'))}
                          </Tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                </>
              )}

              <Dialog
                open={modalIsOpen}
                onClose={closeModal}
                PaperProps={{
                  style: {
                    background: "linear-gradient(233.57deg, rgba(34, 52, 97, 0.25) -5.01%, rgba(11, 10, 31, 0.05) 100%)",
                    opacity: 1,
                    backdropFilter: "blur(500px)",
                    color: "white",
                    minWidth: "500px",
                  }
                }}
              >
                <DialogTitle className="text-white">Candidate Report</DialogTitle>
                <DialogContent dividers className="text-white mt-2">
                  {currentReport ? (
                    <div>
                      <h3 style={{ fontWeight: "bold", marginBottom: "8px" }}>Strengths</h3>
                      <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px" }}>
                        {currentReport.Strengths && currentReport.Strengths.length > 0 ? (
                          currentReport.Strengths.map((strength, index) => (
                            <li key={index}>{strength}</li>
                          ))
                        ) : (
                          <li>No strengths listed.</li>
                        )}
                      </ul>
                      <h3 style={{ fontWeight: "bold", marginBottom: "8px" }}>Weaknesses</h3>
                      <ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "16px" }}>
                        {currentReport.Weaknesses && currentReport.Weaknesses.length > 0 ? (
                          currentReport.Weaknesses.map((weakness, index) => (
                            <li key={index}>{weakness}</li>
                          ))
                        ) : (
                          <li>No weaknesses listed.</li>
                        )}
                      </ul>
                      <h3 style={{ fontWeight: "bold", marginBottom: "8px" }}>Suggestions</h3>
                      <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                        {currentReport.Suggestions && currentReport.Suggestions.length > 0 ? (
                          currentReport.Suggestions.map((suggestion, index) => (
                            <li key={index}>{suggestion}</li>
                          ))
                        ) : (
                          <li>No suggestions listed.</li>
                        )}
                      </ul>
                    </div>
                  ) : (
                    <p>No report available</p>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeModal} color="primary">Close</Button>
                </DialogActions>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
  );
}

export default OrgAdminBulkResumeSummary;